import { Fragment, useEffect, useRef, useState } from "react";
import { AlertTriangle, BusFront, Car, CarFront, Castle, Check, ChevronDown, Globe2, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { API } from "@/api";
import { useLang } from "@/i18n";
import { GoldImage } from "@/components/GoldImage";

// Fotografie reali dal nostro archivio permanente (fonte Google Places).
const HERO_IMG = `${API}/public/cities/roma/attractions/san-pietro/photo`;
const ROME_IMG = `${API}/public/cities/roma/attractions/colosseo/photo`;
const ITALY_IMG = `${API}/public/cities/costiera-amalfitana/attractions/positano/photo`;

/** Hero immersiva del configuratore: etichetta, grande titolo serif, descrizione,
 *  video cinematografico dell'Italia (montaggio Ken Burns sulle nostre fotografie reali)
/** Etichetta piccola oro delle celle preferenze (modulo: mai rimontata a ogni render). */
export const L = ({ children }) => <span className="text-[10px] uppercase tracking-[0.18em] text-amber-500/90 font-semibold">{children}</span>;

/** Cella della barra preferenze (modulo: definirla dentro il builder causava il rimontaggio
 *  degli input a ogni digitazione — il bug del campo budget che perdeva focus/valore). */
export const PrefCell = ({ icon: Icon, label, children, testId }) => (
    <div className="flex items-center gap-3 px-5 py-4" data-testid={testId}>
        <Icon size={17} className="shrink-0 text-amber-400" />
        <div className="min-w-0 flex-1">
            <L>{label}</L>
            <div className="mt-1.5">{children}</div>
        </div>
    </div>
);

/** Campo budget robusto: lo stato conserva solo le cifre; la formattazione con
 *  separatore delle migliaia appare quando il campo non è in digitazione. */
export const BudgetInput = ({ value, onChange, testId = "pref-budget" }) => {
    const { t } = useLang();
    const [focused, setFocused] = useState(false);
    const digits = String(value || "");
    const display = focused ? digits : digits ? Number(digits).toLocaleString("it-IT") : "";
    return (
        <div>
            <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono-accent text-xs text-amber-500/70">€</span>
                <input data-testid={testId} inputMode="numeric" autoComplete="off" className="input-lux !py-2 !pl-8"
                    placeholder={t.builder.budgetPh} value={display}
                    onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                    onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, "").slice(0, 9))} />
            </div>
            <p className="mt-1 text-[10px] leading-snug text-stone-500" data-testid="budget-hint">{t.builder.budgetHint}</p>
        </div>
    );
};

/** Mezzi reali della flotta con capienza dichiarata. */
export const TRANSPORT_OPTIONS = [
    { id: "car", icon: Car, cap: 3 },
    { id: "minivan", icon: CarFront, cap: 8 },
    { id: "sprinter", icon: BusFront, cap: 12 },
];
export const transportCap = (id) => (TRANSPORT_OPTIONS.find((o) => o.id === id) || TRANSPORT_OPTIONS[0]).cap;

/** Selettore elegante del mezzo: nome + capienza, una sola scelta, avviso se il
 *  gruppo supera la capienza (nessuna assegnazione automatica non verificata). */
export const TransportPicker = ({ value, onChange }) => {
    const { t } = useLang();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);
    const sel = TRANSPORT_OPTIONS.find((o) => o.id === value) || TRANSPORT_OPTIONS[0];
    const SelIcon = sel.icon;
    return (
        <div className="relative" ref={ref}>
            <button type="button" data-testid="transport-picker" onClick={() => setOpen((o) => !o)}
                className="input-lux flex w-full items-center justify-between gap-2 !py-2 text-left">
                <span className="flex min-w-0 items-center gap-2">
                    <SelIcon size={15} className="shrink-0 text-amber-400" />
                    <span className="truncate">{t.builder.transports[sel.id]}</span>
                    <span className="shrink-0 font-mono-accent text-[10px] text-stone-500">{t.builder.transportCap.replace("{n}", sel.cap)}</span>
                </span>
                <ChevronDown size={13} className={`shrink-0 text-amber-500/70 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-xl border border-amber-500/30 bg-[#101011] shadow-[0_18px_50px_rgba(0,0,0,0.6)]"
                        data-testid="transport-menu">
                        {TRANSPORT_OPTIONS.map((o) => {
                            const OIcon = o.icon;
                            const active = o.id === sel.id;
                            return (
                                <button key={o.id} type="button" data-testid={`transport-option-${o.id}`}
                                    onClick={() => { onChange(o.id); setOpen(false); }}
                                    className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${active ? "bg-amber-400/15" : "hover:bg-amber-400/5"}`}>
                                    <OIcon size={17} className={active ? "text-amber-300" : "text-stone-400"} />
                                    <span className="min-w-0 flex-1">
                                        <span className={`block text-sm ${active ? "text-amber-100" : "text-stone-200"}`}>{t.builder.transports[o.id]}</span>
                                        <span className="block font-mono-accent text-[10px] text-stone-500">{t.builder.transportCap.replace("{n}", o.cap)}</span>
                                    </span>
                                    {active && <Check size={15} className="shrink-0 text-amber-300" />}
                                </button>
                            );
                        })}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

/** Avviso capienza: gruppo più grande del mezzo scelto → mezzo adeguato o soluzione
 *  personalizzata (richiesta, mai assegnazione automatica di disponibilità). */
export const TransportCapacityNotice = ({ transport, participants, custom, onCustom }) => {
    const { t } = useLang();
    const pax = parseInt(participants, 10) || 1;
    const cap = transportCap(transport);
    if (pax <= cap && !custom) return null;
    const name = t.builder.transports[transport] || t.builder.transports.car;
    return (
        <div className="flex flex-wrap items-center gap-3 border-t border-amber-500/10 px-5 py-3" data-testid="transport-capacity-notice">
            {pax > cap && (
                <>
                    <AlertTriangle size={15} className="shrink-0 text-amber-400" />
                    <p className="min-w-0 flex-1 text-xs leading-relaxed text-amber-200" data-testid="transport-capacity-warning">
                        {t.builder.capWarn.replace("{pax}", pax).replace("{name}", name).replace("{cap}", cap)}{" "}
                        <span className="text-stone-400">{t.builder.capWarnHint}</span>
                    </p>
                </>
            )}
            {custom ? (
                <button type="button" data-testid="transport-custom-ok" onClick={onCustom} title={t.builder.expRemove}
                    className={`${pax > cap ? "" : "flex-1 "}shrink-0 text-left text-xs text-emerald-300 hover:text-stone-300 transition-colors`}>
                    <Check size={13} className="mr-1.5 inline" />{t.builder.capCustomSet}
                </button>
            ) : (
                pax > cap && (
                    <button type="button" data-testid="transport-custom-btn" onClick={onCustom}
                        className="shrink-0 rounded-full border border-amber-500/50 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-200 transition-colors hover:bg-amber-400 hover:text-black">
                        {t.builder.capCustomBtn}
                    </button>
                )
            )}
        </div>
    );
};

/** Stepper sempre visibile del wizard a 5 passi (Fase 2 master prompt):
 *  Destinazioni → Alloggi → Trasporti → Esperienze → Riepilogo. Ogni passo è cliccabile. */
export const WizardStepper = ({ step, onStep }) => {
    const { t } = useLang();
    const steps = t.builder.wizard.steps;
    return (
        <div className="rounded-2xl border border-amber-500/20 bg-[#0b0b0c]/85 px-4 py-4 sm:px-6" data-testid="wizard-stepper">
            <ol className="flex items-center gap-2 overflow-x-auto sm:gap-3">
                {steps.map((s, i) => {
                    const n = i + 1;
                    const done = n < step;
                    const active = n === step;
                    return (
                        <Fragment key={n}>
                            {n > 1 && <li aria-hidden="true" className={`h-px w-5 shrink-0 sm:w-8 ${done || active ? "bg-amber-400/60" : "bg-amber-500/20"}`} />}
                            <li className="shrink-0">
                                <button type="button" data-testid={`wizard-step-${n}`} onClick={() => onStep(n)}
                                    className="group flex items-center gap-2.5 text-left">
                                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 font-mono-accent text-xs font-bold transition-all ${
                                        active ? "border-amber-400 bg-amber-400 text-black shadow-[0_0_18px_rgba(241,211,108,0.35)]"
                                        : done ? "border-amber-400/70 text-amber-300"
                                        : "border-amber-500/30 text-stone-500 group-hover:border-amber-400/60"}`}>
                                        {done ? <Check size={13} strokeWidth={3} /> : n}
                                    </span>
                                    <span className="min-w-0">
                                        <span className={`block whitespace-nowrap text-xs font-semibold ${active ? "text-amber-100" : done ? "text-amber-200/80" : "text-stone-400"}`}>{s.t}</span>
                                        <span className="block whitespace-nowrap text-[10px] text-stone-500 max-sm:hidden">{s.d}</span>
                                    </span>
                                </button>
                            </li>
                        </Fragment>
                    );
                })}
            </ol>
        </div>
    );
};


/** Hero immersiva del configuratore: video cinematografico dell'Italia (montaggio
 *  Ken Burns sulle nostre fotografie reali) con poster/fallback sull'immagine della modalità. */
const HERO_VIDEO = "/assets/video/italy-hero.mp4";
export const BuilderHero = ({ title, sub, heroImg = HERO_IMG }) => {
    const { t } = useLang();
    const videoRef = useRef(null);
    useEffect(() => {
        const v = videoRef.current;
        if (v && window.matchMedia("(prefers-reduced-motion: reduce)").matches) v.pause();
    }, []);
    return (
        <div className="relative overflow-hidden rounded-2xl border border-amber-500/20" data-testid="builder-hero">
            <video ref={videoRef} data-testid="builder-hero-video"
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay muted loop playsInline preload="auto" poster={heroImg}
                aria-hidden="true">
                <source src="/assets/video/italy-hero.webm" type="video/webm" />
                <source src={HERO_VIDEO} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-r from-[#080909]/95 via-[#080909]/70 to-[#080909]/35" />
            <div className="relative px-6 py-10 sm:px-10 sm:py-14 max-w-2xl">
                <p className="font-mono-accent text-[11px] uppercase tracking-[0.4em] text-amber-400">{t.builder.heroLabel}</p>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-amber-50">{title}</h1>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-stone-300">{sub}</p>
            </div>
        </div>
    );
};

/** Le due grandi card fotografiche di modalità (Discover Rome / Tour of Italy).
 *  La selezione cambia davvero il configuratore (navigazione tra le due route). */
export const ModeCards = ({ active }) => {
    const { t } = useLang();
    const navigate = useNavigate();
    const cards = [
        { mode: "roma", to: "/itinerario/roma", icon: Castle, title: t.builder.modeRome, sub: t.builder.modeRomeSub, img: ROME_IMG, testid: "mode-card-rome" },
        { mode: "italy", to: "/itinerario/italia", icon: Globe2, title: t.builder.modeItaly, sub: t.builder.modeItalySub, img: ITALY_IMG, testid: "mode-card-italy" },
    ];
    return (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="builder-mode-cards">
            {cards.map((c) => {
                const isActive = active === c.mode;
                return (
                    <button key={c.mode} data-testid={c.testid} onClick={() => !isActive && navigate(c.to)}
                        className={`group relative flex items-stretch overflow-hidden rounded-2xl border text-left transition-all duration-500 ${
                            isActive
                                ? "border-amber-400/80 shadow-[0_0_32px_rgba(214,180,92,0.22)]"
                                : "border-amber-500/20 hover:border-amber-400/50"
                        }`}>
                        <div className="relative w-2/5 shrink-0 overflow-hidden">
                            <GoldImage src={c.img} alt={c.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0b0b0c]/60" />
                        </div>
                        <div className="flex flex-1 items-center justify-between gap-3 bg-[#0b0b0c]/90 p-5 sm:p-6">
                            <div className="min-w-0">
                                <p className={`font-display text-xl sm:text-2xl ${isActive ? "text-amber-100" : "text-stone-100"}`}>{c.title}</p>
                                <p className="mt-1 text-xs leading-relaxed text-stone-400">{c.sub}</p>
                            </div>
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                                isActive ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/40 text-transparent group-hover:border-amber-400/70"
                            }`} data-testid={`${c.testid}-check`}>
                                <Check size={15} strokeWidth={3} />
                            </span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

/** Pannello laterale premium per aggiungere destinazioni/luoghi (drawer da destra). */
export const CatalogDrawer = ({ open, onClose, title, children }) => (
    <AnimatePresence>
        {open && (
            <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[75] bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="drawer-backdrop" />
                <motion.aside
                    initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                    transition={{ type: "tween", duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
                    className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col border-l border-amber-500/25 bg-[#0e0e0f]"
                    data-testid="catalog-drawer">
                    <div className="flex items-center justify-between border-b border-amber-500/15 px-6 py-5">
                        <h3 className="font-display text-xl text-amber-100">{title}</h3>
                        <button data-testid="drawer-close" onClick={onClose} aria-label="close"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/40 text-amber-300 transition-colors hover:bg-amber-400 hover:text-black">
                            <X size={15} />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
                </motion.aside>
            </>
        )}
    </AnimatePresence>
);
